import { create } from 'zustand';
import { local } from '../lib/storage';

export type Wallpaper = 'cream' | 'mint' | 'night';
export type MotionPref = 'system' | 'reduce' | 'full';

export interface Settings {
  version: 1;
  wallpaper: Wallpaper;
  scanlines: boolean;
  ambientMotion: boolean;
  forceCpuMining: boolean;
  skipBoot: boolean;
  motion: MotionPref;
  coordinatorOverride: string; // Advanced; empty = same-origin /api
  seenWelcome: boolean;
}

export const WALLPAPERS: { id: Wallpaper; name: string; blurb: string }[] = [
  { id: 'cream', name: 'Oatmeal Daydream', blurb: 'Lavender hills and a friendly ghost' },
  { id: 'mint', name: 'Peach Afternoon', blurb: 'A warmer little corner of the day' },
  { id: 'night', name: 'Paper Desk', blurb: 'Oatmeal paper and a quiet grid' },
];

const DEFAULTS: Settings = {
  version: 1,
  wallpaper: 'cream',
  scanlines: false,
  ambientMotion: true,
  forceCpuMining: false,
  skipBoot: false,
  motion: 'system',
  coordinatorOverride: '',
  seenWelcome: false,
};

function migrate(raw: unknown): Settings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULTS };
  const r = raw as Partial<Settings>;
  // v1 is the first schema; unknown keys are dropped, missing keys take defaults.
  const out: Settings = { ...DEFAULTS };
  for (const k of Object.keys(DEFAULTS) as (keyof Settings)[]) {
    if (k in r && typeof r[k] === typeof DEFAULTS[k]) (out as unknown as Record<string, unknown>)[k] = r[k];
  }
  if (!WALLPAPERS.some((w) => w.id === out.wallpaper)) out.wallpaper = 'cream';
  if (!['system', 'reduce', 'full'].includes(out.motion)) out.motion = 'system';
  out.version = 1;
  return out;
}

interface SettingsStore extends Settings {
  set: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  reset: () => void;
}

export const useSettings = create<SettingsStore>((set, get) => ({
  ...migrate(local.get('settings', null)),
  set: (key, value) => {
    set({ [key]: value } as Partial<SettingsStore>);
    const { set: _s, reset: _r, ...plain } = get();
    local.set('settings', plain);
  },
  reset: () => {
    set({ ...DEFAULTS, seenWelcome: true });
    local.set('settings', { ...DEFAULTS, seenWelcome: true });
  },
}));

export const settings = () => useSettings.getState();
